import { Injectable } from '@angular/core';
import { ConfigParamGrpcServiceClient } from '@bp-core/src/lib/proto/config-param.pbsc';

import { GetConfigParam } from '@bp-core/src/lib/proto/config-param.pb';
import { AuthService } from '@bp-core/src/lib/services/portal/auth.service';
import { Int32Value } from '@ngx-grpc/well-known-types';
import { Observable, catchError, combineLatest, of, switchMap, take } from 'rxjs';
import { BpCodes, ConfigCode } from '../models/config-code.model';

@Injectable({
  providedIn: 'root',
})
export class PortalConfigParamService {
  private userId: number | undefined;
  private cache = new Map<string, string | undefined>();

  constructor(
    private configParamGrpcServiceClient: ConfigParamGrpcServiceClient,
    private authService: AuthService,
  ) {
    // Aquí colocar los códigos que se quieran precargar para el usuario actual
    this.authService.userSubject$.subscribe(user => {
      if (this.userId && user?.id === this.userId) return;

      if (user === undefined || (this.userId && user.id !== this.userId)) {
        this.userId = undefined;
        console.log('Clearing config param cache');
        this.cache.clear();
        return;
      }
      this.userId = user.id;

      combineLatest([
        // Aquí colocar los códigos que se quieran precargar

        this.get(BpCodes.localCountryCode),
        this.get(BpCodes.listIncomePatrimonySegmentation),
        this.get(BpCodes.showPaymentButton),
        this.get(BpCodes.showSemiLiquidFlow, this.userId),
        this.get(BpCodes.showBpPatrimonyResume, this.userId),
        // Aquí colocar los códigos que se quieran precargar para el usuario actual
      ]).subscribe();
    });
  }

  /**
   * Obtiene el valor de un código de configuración, si no está en caché lo obtiene del servidor
   *
   * @param {ConfigCode} code
   * @param {number} [userId]
   * @return {*}  {Observable<string>}
   * @memberof PortalConfigParamService
   */
  get(code: ConfigCode, userId?: number): Observable<string> {
    const cachedValue = this.cache.get(code.code);

    if (cachedValue !== undefined) {
      return of(cachedValue);
    }

    return this.fetchFromServer(code, userId);
  }
  /**
   * Fuerza la obtención del valor de un código de configuración desde el servidor
   *
   * @param {ConfigCode} code
   * @param {number} [userId]
   * @return {*}  {Observable<string>}
   * @memberof PortalConfigParamService
   */
  forceGet(code: ConfigCode, userId?: number): Observable<string> {
    return this.fetchFromServer(code, userId);
  }
  /**
   * Obtiene el valor de un código de configuración desde el servidor
   *
   * @private
   * @param {ConfigCode} code
   * @param {number} [userId]
   * @return {*}  {Observable<string>}
   * @memberof PortalConfigParamService
   */
  private fetchFromServer(code: ConfigCode, userId?: number): Observable<string> {
    return this.configParamGrpcServiceClient
      .get(
        new GetConfigParam({
          code: code.code,
          userId: new Int32Value({ value: userId }),
        }),
      )
      .pipe(
        take(1),
        switchMap(value => {
          this.cache.set(code.code, value.value);
          return of(value.value);
        }),

        catchError(error => {
          console.log('Error fetching value for code:', code.code, error);
          // Revisar si esto es correcto
          this.cache.set(code.code, code.default);
          return of(code.default);
        }),
      );
  }

  /**
   * Sets the cache value for the given configuration code.
   * If a userId is provided, the cache key will include the userId.
   * @param code The configuration code.
   * @param value The value to be cached.
   * @param userId Optional. The user ID.
   */
  // private setCache(code: ConfigCode, value: string, userId?: number): void {
  //   if (userId) {
  //     this.cache.set(`${code.code}-${userId}`, value);
  //   } else {
  //     this.cache.set(code.code, value);
  //   }
  // }
}
